/**
 * web worker context toolkit
 */

export function noop() {
  /* no operation*/
}

/**
 * general unique id
 * @param [name]
 * @return {String}
 */
export function guid(name) {
  // crypto.randomUUID();
  return `${(1e5 * Math.random()).toString(36)}-${performance
    .now()
    .toString(36)}${name ? `-${name}` : ''}`;
}

export function hasValue(value) {
  return value !== undefined && value !== null;
}

/**
 * shallow strip of properties with undefined values from `obj`
 * @param obj {Object}
 */
export function stripUndefined(obj) {
  for (const key in obj) {
    if (Object.getOwnPropertyDescriptor(obj, key) && obj[key] === undefined) {
      delete obj[key];
    }
  }

  return obj;
}

/**
 * A Blob is almost a File - it's just missing the two properties below which we will add
 * @param blob {Blob}
 * @param fileName {String}
 * @return {File}
 */
export function blob2File(blob, fileName) {
  return new File([blob], fileName);
}

/**
 * Get bytes ranging from `start` up to but not including `end`
 * @param blob
 * @param start
 * @param end
 * @private
 * @return {Blob}
 */
export function blobSliceChunk(blob, start, end) {
  if (!blob.slice) {
    // API fallback
    blob.slice = blob.mozSlice || blob.webkitSlice || noop;
  }

  return blob.slice(start, end, blob.type);
}

/**
 * Wrapper for native promise exposing public resolve/reject methods
 * and status property.
 * Cold as opposed to hot where resolving is done in the promise belly :)
 */
export class ColdPromise {
  constructor(fn) {
    this.promise = new Promise((resolve, reject) => {
      this.status = 'pending';

      this.resolve = resolve;
      this.reject = reject;
      fn && fn(this.resolve, this.reject);
    });

    this.promise.then(
      () => {
        this.status = 'resolved';
      },
      () => {
        this.status = 'rejected';
      }
    );
  }

  isPending() {
    return this.status === 'pending';
  }

  /**
   * @param onResolve {Function}
   * @param [onReject] {Function}
   * @return {Promise}
   */
  then(onResolve, onReject) {
    return this.promise.then(onResolve, onReject);
  }

  /**
   * @param onReject {Function}
   * @return {Promise}
   */
  catch(onReject) {
    return this.promise.catch(onReject);
  }

  /**
   * @param onFinally {Function}
   * @return {Promise}
   */
  finally(onFinally) {
    return this.promise.then(onFinally, onFinally);
  }
}

/**
 * Task for serial execution inside TaskPool
 * @constructor
 */
export class Task {
  /**
   * @param asyncJob {function} that accepts `resolve`, `reject` arguments
   */
  constructor(asyncJob) {
    this.guid = guid('task');
    /**
     * `this.state` would be resolved with the result of `asyncJob`
     */
    this.state = new ColdPromise();
    this.execute = () => {
      asyncJob(this.state.resolve, this.state.reject);
    };
  }
}

/**
 * Pool of tasks for serial execution
 * @constructor
 */
export class TaskPool {
  constructor() {
    this.pool = [];
  }

  /**
   * @param task {Task}
   * @return {TaskPool}
   */
  remove(task) {
    for (let item, n = 0, N = this.pool.length; n < N; n++) {
      item = this.pool[n];
      if (item.guid === task.guid) {
        this.pool.splice(n, 1);
        break;
      }
    }

    return this;
  }

  /**
   * @return {TaskPool}
   */
  clear() {
    this.pool.splice(0, this.pool.length);
    return this;
  }

  /**
   * @public
   * Add task to pool
   * @param task {Task}
   * @return {TaskPool}
   */
  add(task) {
    task.state.then(
      this._onTaskComplete.bind(this, task),
      this._onTaskComplete.bind(this, task)
    );

    this.pool.push(task);
    if (this.pool.length === 1) {
      task.execute();
    }

    return this;
  }

  /**
   * @public
   * @return {Boolean}
   */
  isBusy() {
    return this.pool.length > 0;
  }

  _onTaskComplete(task) {
    this.remove(task);
    const nextTask = this.pool[0];
    if (nextTask) {
      nextTask.execute();
    }
  }

  static test() {
    const now = Date.now();

    function job(name, resolve /* , reject*/) {
      setTimeout(() => {
        console.log('TaskPool.test-> job done', name);
        resolve(Date.now() - now);
      }, 1e3);
    }

    const pool = new TaskPool();
    const tasks = [
      new Task(job.bind(null, 'task I')),
      new Task(job.bind(null, 'task II')),
      new Task(job.bind(null, 'task III')),
    ];

    for (let task, n = 0, N = tasks.length; n < N; n++) {
      task = tasks[n];
      task.state.then((result) => {
        console.log('TaskPool.test-> task', n, 'finished with result', result);
      });
      pool.add(task);
    }
  }
}

export class Timer {
  /**
   * @param fn {Function}
   * @param [delay] {Number}
   * @constructor
   */
  constructor(fn, delay) {
    this.isActive = false;
    this.setDelay(delay || 0);
    this._timer = null;
    this._fn = () => {
      try {
        fn(...this._args);
      } catch (ignore) {
        // ignore
      }
    };
  }

  /**
   * @param delay {Number}
   * @return {Timer}
   */
  setDelay(delay) {
    this._delay = Math.max(8, delay);
    return this;
  }

  /**
   * @return {Timer}
   */
  trigger(...args) {
    this._args = args;
    this._fn();
    return this;
  }

  /**
   * @return {Timer}
   */
  stop() {
    if (this.isActive) {
      this._timer = clearTimeout(this._timer);
      this.isActive = false;
    }

    return this;
  }

  /**
   * @return {Timer}
   */
  start(...args) {
    this.stop();

    this.isActive = true;
    this._args = args;
    this._timer = setTimeout(this._fn, this._delay);

    return this;
  }

  static test() {
    const now = Date.now();
    const timer = new Timer((message) => {
      console.log(`Timer.test-> ${message}, passed: ${Date.now() - now}`);
    });

    timer.trigger('trigger').setDelay(3e3).start('start');
  }
}

/**
 * will return the index of the closes value to target
 * @param target {Number}
 * @param lowerBound {Number}
 * @param upperBound {Number}
 * @param beats {Array}
 * @returns {Number}
 */
export function findUpperBoundIndexInSorted(
  target,
  lowerBound,
  upperBound,
  beats
) {
  while (beats[lowerBound].time < beats[upperBound].time) {
    const middle = Math.round(lowerBound + (upperBound - lowerBound) / 2) - 1;

    if (target < beats[middle].time) {
      upperBound = middle;
    } else {
      lowerBound = middle + 1;
    }
  }

  return lowerBound;
}
/**
 * find index in array where item comparator evaluates to 0
 * uses binary search
 * assume sorted by condition
 * assume each item conditionally unique
 * @param arr {array}
 * @param comparator {function}
 * @returns {null|number} returns null if not found
 */
export function findIndexInSorted(arr, comparator) {
  let l = 0;
  let h = arr.length;

  while (l < h) {
    const m = Math.trunc((l + h) / 2);
    const cmp = comparator(arr[m], m);
    let setL = false;

    if (cmp === 0) {
      return m;
    } else {
      setL = cmp > 0;
    }

    if (setL) {
      l = m + 1;
    } else {
      h = m;
    }
  }

  return null;
}

/**
 * load font family to the context of web-worker
 * @param fontName {String}
 */
export function workerFontLoader(fontName) {
  // FontFaceSet: https://fonts.googleapis.com/css?family=Roboto:400
  if (self.FontFace) {
    const fontFace = new self.FontFace(
      fontName,
      "url(https://fonts.gstatic.com/s/roboto/v27/KFOmCnqEu92Fr1Mu4mxKKTU1Kg.woff2) format('woff2')"
    );
    self.fonts.add(fontFace);
    return fontFace.load();
  } else {
    return Promise.resolve();
  }
}

/**
 * @param c - Canvas context
 * @param str {string}
 * @param maxWidth {number}
 * @returns {{str: string, width: number}}
 */
export function stringWithEllipsis(c, str, maxWidth) {
  let width = c.measureText(str).width;
  const ellipsis = '…';
  const ellipsisWidth = c.measureText(ellipsis).width;

  if (width <= maxWidth || width <= ellipsisWidth) {
    return { str, width };
  } else {
    let len = str.length;
    while (width >= maxWidth - ellipsisWidth && len-- > 0) {
      str = str.substring(0, len);
      width = c.measureText(str).width;
    }
    return {
      str: str + ellipsis,
      width,
    };
  }
}

/**
 * Serial invocation for async functions.
 * Ignore high demand invocations while async `targetFn` is still working.
 * Its like requestAnimationFrame but for async functions.
 * @param targetFn {function} that returns promise
 * @param resultFn {function} that processes `targetFn` result
 * @returns {(function(): void)}
 */
export function serialInvocation(targetFn, resultFn) {
  let waiting = false;
  let lastCall = 0;

  return function serialInvocationWrapper() {
    const now = performance.now();
    lastCall = now;

    if (!waiting) {
      waiting = true;

      targetFn().then((result) => {
        resultFn(result);
        waiting = false;

        if (lastCall > now) {
          serialInvocationWrapper(); // recursion
        }
      });
    }
  };
}
